import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useClientV2} from "../../../../utils/hooks";
import {GET_STORE_SUBSCRIPTION_SUBSCRIBER_RQ_KEY} from "../constants";

/** Subscriber detail envelope: `GET analytics/.../customer-purchases/:subscriptionId/:subscriberId`. */
export type GetSubscriptionSubscriberApiRawResponse = {
  status: boolean;
  message: string;
  data: GetSubscriptionSubscriberApiResponse;
};

export type SubscriptionSubscriberRecentBilling = {
  id: number;
  reference: string;
  amount: number;
  currency: string;
  status: number;
  statusName: string;
  transactionType: number;
  createdAt: string;
};

export type SubscriptionSubscriberCheckoutResponse = {
  question: string;
  answer?: string | null;
};

export type GetSubscriptionSubscriberApiResponse = {
  id: number;
  status: number;
  statusName: string;
  customerDetails: {
    email: string;
    name: string;
    date: string;
    paymentReference: string;
    paymentChannel: string;
  };
  subscriptionDetails: {
    subscriptionName: string;
    tierName: string;
    price: string;
    currency: string;
    amount: number;
    billingInterval: number;
    billingDuration: number;
    billingCycles: number | null;
    billingCyclesLeft: number | null;
    startDate: string;
    nextBillingDate: string;
    totalSpend: number;
  };
  recentBillings: SubscriptionSubscriberRecentBilling[];
  checkoutFormResponses?: SubscriptionSubscriberCheckoutResponse[];
};

export const useGetSubscriptionSubscriberApi = ({
  subscriberId,
  subscriptionId,
}: {
  subscriberId?: number;
  subscriptionId?: number;
}) => {
  const client = useClientV2();

  return useQuery<
    GetSubscriptionSubscriberApiResponse | null,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [
      GET_STORE_SUBSCRIPTION_SUBSCRIBER_RQ_KEY,
      String(subscriptionId),
      String(subscriberId),
    ],
    queryFn: async () => {
      if (!subscriptionId || !subscriberId) {
        return null;
      }

      const envelope = (await client(
        `analytics/subscriptions/customer-purchases/${subscriptionId}/${subscriberId}`
      )) as GetSubscriptionSubscriberApiRawResponse;

      const payload = envelope?.data;
      if (!payload || typeof payload !== "object") {
        return null;
      }

      return {
        ...payload,
        recentBillings: payload.recentBillings ?? [],
      };
    },
    enabled: !!subscriptionId && !!subscriberId,
  });
};
